import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/* ══════════════════════════════════════════════════════════════════
   CookieConsentContext — Gestion du consentement RGPD SchoolSenses
   ══════════════════════════════════════════════════════════════════
   API exposée :
     const { consent, openBanner, setConsent, reset, isVisible } = useCookieConsent();

     consent    → "accepted" | "rejected" | null (pas encore choisi)
     isVisible  → bandeau actuellement affiché ?
     openBanner()          → réaffiche le bandeau (ex. bouton "Gérer mes cookies")
     setConsent("accepted" | "rejected")
     reset()               → efface le choix + rouvre le bandeau

   Stockage : localStorage "ss_cookie_consent"
     { version, choice, date }
   ══════════════════════════════════════════════════════════════════ */

const STORAGE_KEY = "ss_cookie_consent";
const STORAGE_VERSION = "1";

/* ── Cookie fallback ────────────────────────────────────────────
   Si localStorage est indisponible (navigation privée Safari,
   quota, extension…), le consentement est persiste dans un
   cookie first-party `ss_cookie_consent` (durée 13 mois – max RGPD).
   ──────────────────────────────────────────────────────────────── */
const CONSENT_COOKIE_MAX_AGE = 60 * 60 * 24 * 395; // ~13 mois

function writeConsentCookie(value) {
  try {
    const encoded = encodeURIComponent(JSON.stringify(value));
    document.cookie = `${STORAGE_KEY}=${encoded}; path=/; max-age=${CONSENT_COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch { /* ignore */ }
}

function readConsentCookie() {
  try {
    const match = new RegExp(`(?:^|; )${STORAGE_KEY}=([^;]*)`).exec(document.cookie);
    if (!match) return null;
    return JSON.parse(decodeURIComponent(match[1]));
  } catch {
    return null;
  }
}

function removeConsentCookie() {
  try {
    document.cookie = `${STORAGE_KEY}=; path=/; max-age=0`;
  } catch { /* ignore */ }
}

/**
 * Clés localStorage/sessionStorage considérées comme NON essentielles
 * (mesure d'audience, tracking visiteur). Elles sont purgées lors d'un refus.
 * Ne JAMAIS inclure : token, refresh, user, session_id, prefs.*, theme_*,
 * ss_cookie_consent (le consentement lui-même).
 */
const NON_ESSENTIAL_KEYS = [
  "ss_demo_visitor",
  "ss_demo_clicks",
];

/** IDs de tags Google — chargés dynamiquement seulement si consentement */
const GA_MEASUREMENT_ID = "G-VM5Z0ZLYEG";
const GTM_ID = "GTM-TFXH8MS6";

/** Domaines de cookies tiers posés par GA/GTM/Stripe — purgés lors d'un refus */
const NON_ESSENTIAL_COOKIES = [
  "_ga",
  `_ga_${GA_MEASUREMENT_ID.replace(/^G-/, "")}`,
  "_gid",
  "_gat",
  "_gat_gtag_" + GA_MEASUREMENT_ID.replace(/-/g, "_"),
  "__stripe_mid",
  "__stripe_sid",
  "ss_interest",
  "_vercel_speed_insights",
  "_vercel_analytics",
];

function clearCookie(name) {
  if (typeof document === "undefined") return;
  const hostname = window.location.hostname;
  // Supprime en testant plusieurs domaines/paths possibles
  const domains = ["", hostname, `.${hostname}`];
  // Pour GA, le cookie est souvent posé sur le domaine racine (ex: .schoolsenses.com)
  const parts = hostname.split(".");
  if (parts.length > 2) domains.push("." + parts.slice(-2).join("."));
  // Certains cookies (ex. ss_interest d'un ancien build) ont pu être posés
  // sur plusieurs paths — on tente les plus courants.
  const paths = ["/", window.location.pathname || "/"];
  for (const d of domains) {
    for (const p of paths) {
      // Max-Age=0 ET expires dans le passé (compat navigateurs anciens)
      const base = `${name}=; Max-Age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${p}`;
      document.cookie = d ? `${base}; domain=${d}` : base;
      document.cookie = d ? `${base}; domain=${d}; SameSite=Lax` : `${base}; SameSite=Lax`;
    }
  }
}

function purgeNonEssential() {
  try {
    for (const k of NON_ESSENTIAL_KEYS) {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    }
    // Résidu d'anciens builds : ss_interest était posé comme cookie.
    // Il est désormais en sessionStorage, mais on nettoie le cookie legacy.
    try { sessionStorage.removeItem("ss_interest"); } catch { /* ignore */ }
    for (const name of NON_ESSENTIAL_COOKIES) {
      clearCookie(name);
    }
  } catch {
    /* ignore */
  }
}

/**
 * Injection dynamique de Google Tag Manager + GA4 après consentement.
 * Idempotent : ne charge qu'une fois par session de page.
 */
let _gaLoaded = false;
function loadAnalytics() {
  if (_gaLoaded || typeof document === "undefined") return;
  _gaLoaded = true;

  // GA4 (gtag.js)
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, { anonymize_ip: true });

  const gaScript = document.createElement("script");
  gaScript.async = true;
  gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(gaScript);

  // GTM
  (function (w, d, s, l, i) {
    w[l] = w[l] || [];
    w[l].push({ "gtm.start": Date.now(), event: "gtm.js" });
    const f = d.getElementsByTagName(s)[0];
    const j = d.createElement(s);
    const dl = l !== "dataLayer" ? "&l=" + l : "";
    j.async = true;
    j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
    f.parentNode.insertBefore(j, f);
  })(window, document, "script", "dataLayer", GTM_ID);
}

const CookieConsentContext = createContext(null);

function readStored() {
  // 1. Tente localStorage
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.version === STORAGE_VERSION &&
          (parsed?.choice === "accepted" || parsed?.choice === "rejected")) {
        return parsed.choice;
      }
    }
  } catch (err) {
    console.warn("[CookieConsent] localStorage indisponible, tentative cookie fallback.", err);
  }
  // 2. Fallback : cookie first-party
  try {
    const parsed = readConsentCookie();
    if (parsed?.version === STORAGE_VERSION &&
        (parsed?.choice === "accepted" || parsed?.choice === "rejected")) {
      return parsed.choice;
    }
  } catch { /* ignore */ }
  return null;
}

export function CookieConsentProvider({ children }) {
  const [consent, setConsentState] = useState(() => readStored());
  const [isVisible, setIsVisible] = useState(false);

  // ──────────────────────────────────────────────────────────────────
  // Purge défensive : tant que l'utilisateur n'a PAS accepté (null ou
  // "rejected"), aucun cookie non essentiel ne doit subsister. On purge :
  //   1. au montage (résidus d'un ancien build ou d'une autre session)
  //   2. à chaque changement de consentement
  //   3. périodiquement pendant les 10 premières secondes (cas d'un
  //      script tiers qui tenterait de reposer un cookie en asynchrone)
  //   4. sur événement "storage" (autre onglet qui modifie le consentement)
  // ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (consent === "accepted") {
      loadAnalytics();
      return undefined;
    }
    // null (pas encore choisi) OU "rejected" → on purge
    purgeNonEssential();

    // Afficher le bandeau si aucun choix stocké
    let bannerTimer;
    if (consent == null) {
      bannerTimer = setTimeout(() => setIsVisible(true), 400);
    }

    // Re-purge défensive (3 passes sur 9s) pour attraper les cookies
    // qu'un script tiers chargé tardivement aurait pu reposer.
    const repurgeTimers = [1500, 4000, 9000].map((ms) =>
      setTimeout(purgeNonEssential, ms)
    );

    // Synchronisation inter-onglets : si un autre onglet change le
    // consentement ou pose un cookie surveillé, on réagit.
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        const next = readStored();
        setConsentState(next);
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      if (bannerTimer) clearTimeout(bannerTimer);
      repurgeTimers.forEach(clearTimeout);
      window.removeEventListener("storage", onStorage);
    };
  }, [consent]);

  const setConsent = useCallback((choice) => {
    if (choice !== "accepted" && choice !== "rejected") return;
    const payload = { version: STORAGE_VERSION, choice, date: new Date().toISOString() };
    let persisted = false;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      persisted = true;
    } catch (err) {
      console.warn("[CookieConsent] Impossible d'écrire dans localStorage.", err);
    }
    // Toujours écrire le cookie fallback (garantie de persistance)
    writeConsentCookie(payload);
    if (!persisted) {
      console.info("[CookieConsent] Consentement sauvegardé via cookie fallback.");
    }
    if (choice === "accepted") {
      loadAnalytics();
    } else if (choice === "rejected") {
      purgeNonEssential();
    }
    setConsentState(choice);
    setIsVisible(false);
  }, []);

  const openBanner = useCallback(() => setIsVisible(true), []);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch { /* ignore */ }
    removeConsentCookie();
    setConsentState(null);
    setIsVisible(true);
  }, []);

  const value = useMemo(
    () => ({ consent, isVisible, setConsent, openBanner, reset }),
    [consent, isVisible, setConsent, openBanner, reset]
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

export function useCookieConsent() {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    // Fallback no-op pour éviter les crashs si un composant est rendu hors provider
    return {
      consent: null,
      isVisible: false,
      setConsent: () => {},
      openBanner: () => {},
      reset: () => {},
    };
  }
  return ctx;
}

export default CookieConsentContext;
